<template>
    <div class="space-y-6">
        <!-- Answer summary -->
        <div class="divide-y divide-slate-100 dark:divide-slate-700 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
            <div
                v-for="row in summaryRows"
                :key="row.label"
                class="grid grid-cols-1 sm:grid-cols-2 gap-1 px-4 py-3 bg-white dark:bg-slate-800"
            >
                <span class="text-sm text-slate-500 dark:text-slate-400">{{ row.label }}</span>
                <span class="text-sm text-slate-800 dark:text-slate-100 whitespace-pre-line">{{ row.value }}</span>
            </div>
        </div>

        <!-- Fee summary -->
        <div class="rounded-lg bg-orange-50 dark:bg-orange-500/10 border border-orange-200 dark:border-orange-500/30 p-4">
            <div class="space-y-1.5 text-sm text-slate-700 dark:text-slate-200">
                <div class="flex justify-between">
                    <span>Total booking fee for this trip</span>
                    <span class="font-semibold">${{ formatMoney(quotedTotal) }}</span>
                </div>
                <template v-if="legacyPerks">
                    <div v-if="creditApplied > 0" class="flex justify-between text-slate-500 dark:text-slate-400">
                        <span>Your 10xT+ credit</span>
                        <span>−${{ formatMoney(creditApplied) }}</span>
                    </div>
                    <div v-if="legacyDiscount > 0" class="flex justify-between text-slate-500 dark:text-slate-400">
                        <span>Member discount ({{ Math.round(legacyPerks.discount_rate * 100) }}%)</span>
                        <span>−${{ formatMoney(legacyDiscount) }}</span>
                    </div>
                    <div class="flex justify-between border-t border-orange-200 dark:border-orange-500/30 pt-1.5">
                        <span>Your total after member perks</span>
                        <span class="font-semibold">${{ formatMoney(discountedTotal) }}</span>
                    </div>
                </template>
                <div class="flex justify-between">
                    <span>Due now — non-refundable search fee</span>
                    <span class="font-semibold">${{ formattedDeposit }}</span>
                </div>
                <div class="flex justify-between border-t border-orange-200 dark:border-orange-500/30 pt-1.5">
                    <span>Balance when we complete your booking</span>
                    <span class="font-semibold">${{ formatMoney(balanceDue) }}</span>
                </div>
            </div>
            <p class="text-xs text-slate-500 dark:text-slate-400 mt-2">
                Your search fee is credited toward the total if we find availability
                — if we can't, you owe nothing more. You'll be taken to Stripe to
                pay the search fee securely.
            </p>
        </div>

        <!-- Terms consent -->
        <label class="flex items-start gap-3 cursor-pointer">
            <input
                type="checkbox"
                v-model="form.terms_accepted"
                class="mt-0.5 h-4 w-4 rounded border-slate-300 text-orange-500 focus:ring-orange-500 dark:border-slate-600 dark:bg-slate-700"
            />
            <span class="text-sm text-slate-600 dark:text-slate-300">
                I agree to the
                <a
                    :href="termsUrl"
                    target="_blank"
                    rel="noopener"
                    class="underline text-slate-800 dark:text-slate-100 hover:text-orange-600"
                    @click.stop
                >10xTravel Award Booking Terms &amp; Conditions</a>.
            </span>
        </label>
        <p v-if="termsError" class="text-sm text-red-600 dark:text-red-400 -mt-3">
            {{ termsError }}
        </p>
    </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
    form: { type: Object, required: true },
    errors: { type: Object, default: null },
    depositAmount: { type: Number, required: true },
    feeSchedule: { type: Object, required: true },
    legacyPerks: { type: [Object, null], default: null },
    termsUrl: { type: String, required: true },
    tripTypeOptions: { type: Array, required: true },
    cabinOptions: { type: Array, required: true },
});

const formattedDeposit = computed(() => props.depositAmount.toFixed(2));

const formatMoney = (value) =>
    Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Mirrors AwardBookingFeeCalculator: first passenger + 2nd/3rd rate + 4th-and-up
// rate, plus per-stop-per-person beyond the included stops for multi-city.
const quotedTotal = computed(() => {
    const isMultiCity = ['multi_city', 'multi_city_extended'].includes(props.form.trip_type);
    const rates = props.feeSchedule[isMultiCity ? 'multi_city' : props.form.trip_type];
    if (!rates) {
        return props.depositAmount;
    }

    const travelers = Math.max(1, Number(props.form.travelers_count) || 1);
    let total =
        Number(rates.first) +
        Math.min(Math.max(travelers - 1, 0), 2) * Number(rates.second_third) +
        Math.max(travelers - 3, 0) * Number(rates.additional);

    if (isMultiCity && props.form.multi_city_stop_count) {
        const extraStops = Math.max(
            0,
            Number(props.form.multi_city_stop_count) - Number(props.feeSchedule.included_stops)
        );
        total += extraStops * Number(props.feeSchedule.extra_stop_per_person) * travelers;
    }

    return total;
});

// Legacy 10xT+ preview, mirroring feeBreakdown(): credit first, then the
// percentage discount on what remains, then the deposit credit.
const creditApplied = computed(() =>
    props.legacyPerks ? Math.min(Number(props.legacyPerks.credit_remaining) || 0, quotedTotal.value) : 0
);

const legacyDiscount = computed(() =>
    props.legacyPerks
        ? Math.round((quotedTotal.value - creditApplied.value) * props.legacyPerks.discount_rate * 100) / 100
        : 0
);

const discountedTotal = computed(() =>
    Math.max(0, Math.round((quotedTotal.value - creditApplied.value - legacyDiscount.value) * 100) / 100)
);

const balanceDue = computed(() => Math.max(0, discountedTotal.value - props.depositAmount));

const termsError = computed(() => {
    const fieldErrors = props.errors?.terms_accepted;
    return Array.isArray(fieldErrors) ? fieldErrors[0] : null;
});

const yesNo = (value) => (value ? 'Yes' : 'No');

const optionLabel = (options, value) =>
    options.find((option) => option.value === value)?.label || value || '—';

// Mirrors AwardBookingRequest::tripLengthSummary(): either bound may be
// blank, and the row is skipped when both are.
const tripLengthLabel = (minimum, maximum) => {
    const hasMinimum = minimum !== '' && minimum !== null;
    const hasMaximum = maximum !== '' && maximum !== null;

    if (hasMinimum && hasMaximum) {
        return `${minimum}–${maximum} days`;
    }
    if (hasMinimum) {
        return `At least ${minimum} days`;
    }
    if (hasMaximum) {
        return `Up to ${maximum} days`;
    }
    return null;
};

const summaryRows = computed(() => {
    const form = props.form;
    const isMultiCity = ['multi_city', 'multi_city_extended'].includes(form.trip_type);
    const isRoundTrip = form.trip_type === 'round_trip';

    const rows = [
        { label: 'Trip type', value: optionLabel(props.tripTypeOptions, form.trip_type) },
        { label: 'Travelers', value: String(form.travelers_count || '—') },
        { label: 'Home airport', value: form.home_airport || '—' },
        { label: 'Willing to position', value: yesNo(form.willing_to_position) },
    ];

    if (isMultiCity) {
        rows.push({ label: 'Multi-city stops', value: form.multi_city_stops || '—' });
        if (form.trip_type === 'multi_city_extended') {
            rows.push({ label: 'Total stops', value: String(form.multi_city_stop_count || '—') });
        }
    } else {
        rows.push({ label: 'Arrival airport', value: form.arrival_airport || '—' });
    }

    rows.push({
        label: 'Departure date',
        value: `${form.departure_date || '—'}${form.departure_date_flexible ? ` (flexible — ${form.departure_flexibility})` : ''}`,
    });

    if (isRoundTrip) {
        rows.push({
            label: 'Return date',
            value: `${form.return_date || '—'}${form.return_date_flexible ? ` (flexible — ${form.return_flexibility})` : ''}`,
        });
    }

    rows.push(
        { label: 'Same flight for all travelers', value: yesNo(form.same_flight_required) },
        {
            label: 'Countries to avoid',
            value: form.has_travel_restrictions ? form.travel_restrictions || 'Yes' : 'None',
        },
        {
            label: 'Preferred cabin(s)',
            value: form.preferred_cabins
                .map((cabin) => optionLabel(props.cabinOptions, cabin))
                .join(', ') || '—',
        }
    );

    const tripLength = tripLengthLabel(form.min_trip_length_days, form.max_trip_length_days);
    if (tripLength) {
        rows.push({ label: 'Trip length', value: tripLength });
    }

    rows.push(
        { label: 'Points & miles balances', value: form.points_balances || '—' },
        { label: 'Willing to purchase miles', value: yesNo(form.willing_to_purchase_miles) }
    );

    if (form.additional_notes) {
        rows.push({ label: 'Anything else', value: form.additional_notes });
    }
    if (form.promo_code) {
        rows.push({ label: 'Promo code', value: form.promo_code });
    }

    return rows;
});
</script>
